import { inject, Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router, RouterStateSnapshot } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { AuthService } from '@auth0/auth0-angular';
import { ClaimKeys } from '../constants';
import { UserApiClient } from '../api-clients';
import { AuthStateService } from './auth-state-service';
import { environment } from 'src/environments';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate, CanActivateChild {
  private readonly _router = inject(Router);

  private readonly _authService = inject(AuthService);

  private readonly _userApiClient = inject(UserApiClient);

  private readonly _authState = inject(AuthStateService);

  private readonly lastLoginUserStorageKey = 'last-login-recorded-user-id';

  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.getAccessTokenOrLogin(next, state.url).pipe(
      switchMap(token => {
        if (token == null) return of(false);

        const jwtHelper = new JwtHelperService();
        const authData: Record<string, unknown> | null = jwtHelper.decodeToken<Record<string, unknown>>(token);
        if (authData === null) return of(false);

        // Clear the stored signup hint after authentication; eligibility is persisted server-side.
        sessionStorage.removeItem('promoCode');
        this._authState.setAuthData(authData);
        // console.log('Auth data:', authData); // TODO: DIT
        const registrationCompletedClaim = authData[ClaimKeys.registrationCompleted];
        const registrationCompleted: boolean = this.readBooleanClaim(registrationCompletedClaim);
        const hasNeerProviderPurchasedClaim = this.hasNeerProviderPurchasedClaim(authData);
        const isExistingUserWithoutNeerProviderClaim = !hasNeerProviderPurchasedClaim;
        const neerProviderPurchased: boolean = this.readNeerProviderPurchasedClaim(authData);

        const emailVerified = this.readBooleanClaim(authData[ClaimKeys.emailVerified]);

        if (!emailVerified) {
          this._router.navigate(['/account/verify']);
          return of(false);
        }

        if (this._authState.hasAdministrationRole()) {
          this.recordLastLogin(authData);
          // Administration roles do not require the doctor registration or Provider purchase workflow.
          return of(true);
        }

        if (!registrationCompleted) {
          return this._userApiClient.getBusiness().pipe(
            tap((): void => this.recordLastLogin(authData)),
            map(() => {
              this._router.navigate(['/account/profile']);
              return false;
            }),
            catchError(() => {
              this._router.navigate(['/account/profile']);
              return of(false);
            })
          );
        }

        this.recordLastLogin(authData);

        if (!isExistingUserWithoutNeerProviderClaim && !neerProviderPurchased) {
          if (this.isNeerProviderPackageRoute(state.url)) {
            return of(true);
          }

          this.navigateToNeerProviderPlans();
          return of(false);
        }

        return of(true);
      }),
      shareReplay(1)
    );
  }

  canActivateChild(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.canActivate(next, state);
  }

  private navigateToNeerProviderPlans(): void {
    this._router.navigate(['/account/package']);
  }

  private isNeerProviderPackageRoute(url: string): boolean {
    return url === '/account/package' || url.startsWith('/account/package?');
  }

  private getAccessTokenOrLogin(next: ActivatedRouteSnapshot, loginTarget: string): Observable<string | null> {
    return this._authService.getAccessTokenSilently().pipe(
      catchError(err => {
        console.warn('Failed to get access token, now trying to redirect to login portal', err);
        const promoCode = next.queryParamMap.get('promoCode') ?? sessionStorage.getItem('promoCode');
        const signupHint = promoCode == null ? undefined : 'signup';
        return this._authService
          .loginWithRedirect({
            appState: {
              target: loginTarget,
            },
            authorizationParams: {
              audience: environment.auth0.audience,
              scope: environment.auth0.scope,
              'ext-promoCode': promoCode ?? undefined,
              screen_hint: signupHint,
            },
          })
          .pipe(map(() => null));
      })
    );
  }

  private recordLastLogin(authData: Record<string, unknown>): void {
    const userId = authData['sub'];
    if (typeof userId !== 'string' || userId === '') {
      return;
    }

    if (sessionStorage.getItem(this.lastLoginUserStorageKey) === userId) {
      return;
    }

    sessionStorage.setItem(this.lastLoginUserStorageKey, userId);
    this._userApiClient
      .updateLastLoginDate()
      .pipe(catchError(() => of(void 0)))
      .subscribe();
  }

  private hasNeerProviderPurchasedClaim(authData: Record<string, unknown>): boolean {
    if (authData[ClaimKeys.neerProviderPurchased] != null) {
      return true;
    }

    const appMetadata = authData[ClaimKeys.app_metadata];
    return (
      typeof appMetadata === 'object' &&
      appMetadata !== null &&
      (appMetadata as Record<string, unknown>)['neer_provider_purchased'] != null
    );
  }

  private readNeerProviderPurchasedClaim(authData: Record<string, unknown>): boolean {
    const flatClaimValue = authData[ClaimKeys.neerProviderPurchased];

    if (flatClaimValue != null) {
      return this.readBooleanClaim(flatClaimValue);
    }

    const appMetadata = authData[ClaimKeys.app_metadata];
    if (typeof appMetadata === 'object' && appMetadata !== null) {
      return this.readBooleanClaim((appMetadata as Record<string, unknown>)['neer_provider_purchased']);
    }

    return false;
  }

  private readBooleanClaim(value: unknown): boolean {
    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'string') {
      return value.toLowerCase() === 'true';
    }

    return false;
  }
}
